import { Vault } from "@padloc/core/src/vault";
import { saveFile } from "@padloc/core/src/platform";
import { stringToBytes } from "@padloc/core/src/encoding";
import JSZip from "jszip";
import { CSV, PBES2 } from "../lib/import";
import { supportedFormats, asCSV, asPBES2Container } from "../lib/export";
import { app } from "../globals";
import { prompt } from "../lib/dialog";
import { Select } from "./select";
import { Dialog } from "./dialog";
import { html } from "lit";
import { fetchStrapiData, replaceDynamicValueString } from "../lib/util";
import { customElement, query, state } from "lit/decorators.js";
import { ExportDataDialogType } from "../../types/strapi/export-data-dialog";
import { StrapiCommonType } from "../../types/strapi/strapi.type";

@customElement("pl-export-dialog")
export class ExportDialog extends Dialog<void, void> {
    @query("#formatSelect")
    private _formatSelect: Select<string>;
    @query("#vaultSelect")
    private _vaultSelect: Select<Vault | string>;
    @state()
    private strapiData: ExportDataDialogType;
    @state()
    private ALL_VAULTS_OPTION: string;

    @state()
    private enStrapiData: StrapiCommonType;

    @state()
    private skStrapiData: StrapiCommonType;

    connectedCallback() {
        super.connectedCallback();
        this.getStrapiData();
    }

    async getStrapiData() {
        const res = await fetchStrapiData("export-data-dialog");
        this.enStrapiData = res?.data?.data?.find((item: any) => item?.attributes?.locale === "en");
        this.skStrapiData = res?.data?.data?.find((item: any) => item?.attributes?.locale === "sk");
    }

    updated(changes: Map<string, any>) {
        super.updated(changes);
    }

    renderContent() {
        const vault = this._vaultSelect?.value;
        this.strapiData =
            this.lang === "en"
                ? this.enStrapiData?.attributes?.exportDataDialog
                : this.skStrapiData?.attributes?.exportDataDialog;
        this.ALL_VAULTS_OPTION = this.strapiData?.allVaults;
        const itemCount =
            vault instanceof Vault
                ? vault.items.size
                : app.vaults.reduce((currentItemCount, appVault) => appVault.items.size + currentItemCount, 0);

        return html`
            <div class="padded vertical spacing layout">
                <h1 class="big margined text-centering">${this.strapiData?.exportData}</h1>

                <pl-select
                    id="vaultSelect"
                    .options=${[this.ALL_VAULTS_OPTION, ...app.vaults].map((v) => ({ value: v }))}
                    .label=${this.strapiData.targetVault}
                    @change=${() => this.requestUpdate()}
                >
                </pl-select>

                <pl-select
                    id="formatSelect"
                    .options=${supportedFormats}
                    .label=${this.strapiData?.format}
                    @change=${() => this.requestUpdate()}
                ></pl-select>

                <div class="small padded" ?hidden=${this._formatSelect && this._formatSelect.value !== CSV.value}>
                    ${this.strapiData?.csvWarning}
                </div>

                <div class="horizontal evenly stretching spacing layout">
                    <pl-button class="primary" @click=${() => this._export()}>
                        ${replaceDynamicValueString(this.strapiData?.exportItems, itemCount.toString())}
                    </pl-button>
                    <pl-button @click=${this.dismiss}> ${this.strapiData?.cancel} </pl-button>
                </div>
            </div>
        `;
    }

    async show() {
        await this.updateComplete;
        this._formatSelect.value = PBES2.value;
        this._vaultSelect.value = app.mainVault!;
        return super.show();
    }

    private async _exportData(vault: Vault, password: string) {
        const items = [...vault.items];
        const date = new Date().toISOString().substring(0, 10);
        let data = "";
        let fileName = "";
        let type = "text/plain";

        switch (this._formatSelect.value) {
            case CSV.value:
                data = await asCSV(items);
                fileName = `${vault.name.replace(/ /g, "_")}_${date}.csv`;
                type = "text/csv";
                break;

            case PBES2.value:
                this.open = false;
                data = await asPBES2Container(items, password);
                fileName = `${vault.name.replace(/ /g, "_")}_${date}.pbes2`;
                break;

            default:
                return;
        }

        return {
            data,
            fileName,
            type,
        };
    }

    private async _export() {
        const vaultOption = this._vaultSelect.value!;

        // Ask for password once
        let password = "";
        if (this._formatSelect.value === PBES2.value) {
            this.open = false;
            password = await prompt(this.strapiData?.pleaseChoosePassword, {
                title: this.strapiData?.choosePassword,
                type: "password",
                placeholder: "Enter Password",
                validate: async (val) => {
                    if (!val) {
                        throw this.strapiData?.pleaseChoosePasswordError;
                    }
                    return val;
                },
            });

            if (!password) {
                this.open = true;
                return;
            }

            const repeated = await prompt(this.strapiData?.pleaseRepeatPassword, {
                title: this.strapiData?.choosePassword,
                type: "password",
                placeholder: "Repeat Password",
                validate: async (val) => {
                    if (val !== password) {
                        throw this.strapiData?.passwordNotRepeatedCorrectly;
                    }
                    return val;
                },
            });

            if (!repeated) {
                this.open = true;
                return;
            }
        }

        if (vaultOption === this.ALL_VAULTS_OPTION) {
            const zip = new JSZip();

            const date = new Date().toISOString().substring(0, 10);
            let zipFileName = `All_Vaults_${date}.zip`;
            let zipFileType = "application/zip";

            for (const vault of app.vaults) {
                const result = await this._exportData(vault, password);
                if (result) {
                    const { data, fileName } = result;
                    zip.file(fileName, stringToBytes(data));
                }
            }

            const zipData = await zip.generateAsync({ type: "uint8array" });

            saveFile(zipFileName, zipFileType, zipData);
        } else {
            const result = await this._exportData(this._vaultSelect.value! as Vault, password);
            if (result) {
                const { data, fileName, type } = result;
                saveFile(fileName, type, stringToBytes(data));
            }
        }

        this.done();
    }
}
